import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { getDateFormatOptions, getDateFormat, DATE_FORMATS } from '@gameday/utils/languageHelpers';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';
import useTracking from '@gameday/utils/hooks/useTracking';
import { getVideoPlayerState } from '@gameday/components/VideoPlayer/store/selectors/getVideoPlayerInfo';
import { formatInstructionSet } from '../../VideoPlayer/utils/normalizationHelper';
import {
    HighlightCardListWrapper,
    ContentCardWrapper,
    ContentCardSubtitleWrapper,
    HeadlineWrapper,
    ContentCardTitleWrapper,
    UpNextWrapper
} from '../HighlightsPlaylist.style';

const formatInsStr = '{formatInstructions}';
const defaultImageProps = {
    width: '140px',
    height: '83px',
    borderRadius: '8px'
};

const CardHeadline = ({ highlight, upNext, language }) => {
    const translate = useTranslate();

    const upNextLabel = translate('label_up_next', 'Up Next');
    const duration = highlight?.duration?.substring(3);

    const options = getDateFormatOptions(language);
    const dateFormat = getDateFormat(language, DATE_FORMATS.LONG);

    const publishDate = highlight?.date
        ? format(new Date(highlight.date), dateFormat, options)
        : '';
    const subtitle = [duration, publishDate].join(' • ');
    return (
        <HeadlineWrapper>
            {upNext && <UpNextWrapper>{upNextLabel}</UpNextWrapper>}
            <ContentCardTitleWrapper>{highlight?.blurb}</ContentCardTitleWrapper>
            <ContentCardSubtitleWrapper>{subtitle}</ContentCardSubtitleWrapper>
        </HeadlineWrapper>
    );
};

CardHeadline.propTypes = {
    highlight: PropTypes.object,
    upNext: PropTypes.bool,
    language: PropTypes.string
};

const HighlightCardList = ({ highlights, handleCardClick, nowPlayingIndex }) => {
    const translate = useTranslate();
    const nowPlayingLabel = translate('label_now_playing', 'Now Playing');
    const Tracking = useTracking();
    const { language } = useSelector(getVideoPlayerState);

    const handleHighlightClick = (e, highlight) => {
        const { title } = highlight;

        Tracking.track.track_click({
            componentName: ['Video', 'Highlight Card'],
            text: title,
            activity: 'Highlight Card Click'
        });

        handleCardClick(highlight);
    };

    const handleOnKeyDown = (e, highlight) => {
        const isActionable =
            e.key.toLowerCase() === "enter" ||
            e.key.toLowerCase().trim() === "";

        if (isActionable) {
            handleHighlightClick(e, highlight);
        }
    };

    return (
        <HighlightCardListWrapper>
            {highlights?.map((highlight, index) => {
                return (
                    <ContentCardWrapper
                        key={`${highlight?.content_id}-${index}`}
                        imageProps={{ ...defaultImageProps, src: highlight?.templateUrl?.replace(formatInsStr, formatInstructionSet['1x']) }}
                        headline={<CardHeadline highlight={highlight} upNext={nowPlayingIndex + 1 === index} language={language} />}
                        handlers={{
                            onClick: (e) => handleHighlightClick(e, highlight),
                            onKeyDown: (e) => handleOnKeyDown(e, highlight),
                        }}
                        isActive= {index === nowPlayingIndex}
                        isActiveText={nowPlayingLabel}
                        ariaLabel={highlight?.blurb}
                    />
                );
            }
            )}
        </HighlightCardListWrapper>
    );
};

HighlightCardList.propTypes = {
    highlights: PropTypes.array,
    handleCardClick: PropTypes.func,
    nowPlayingIndex: PropTypes.number
};

export default HighlightCardList;
